import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Package,
  TrendingUp,
  DollarSign,
  ShoppingCart,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  Eye
} from 'lucide-react'

// Mock data for demonstration
const stats = [
  {
    title: 'Total Products',
    value: '1,234',
    change: '+12%',
    isPositive: true,
    icon: Package,
    color: 'from-blue-500 to-blue-600'
  },
  {
    title: 'Total Sales',
    value: '₹4,52,890',
    change: '+23%',
    isPositive: true,
    icon: DollarSign,
    color: 'from-green-500 to-green-600'
  },
  {
    title: 'Orders Today',
    value: '89',
    change: '+8%',
    isPositive: true,
    icon: ShoppingCart,
    color: 'from-purple-500 to-purple-600'
  },
  {
    title: 'Revenue Growth',
    value: '18.5%',
    change: '-2%',
    isPositive: false,
    icon: TrendingUp,
    color: 'from-primary-500 to-primary-600'
  },
]

const recentProducts = [
  { id: 1, name: 'India Gate Basmati Rice 5kg', price: 599, stock: 150, sales: 234 },
  { id: 2, name: 'Everest Garam Masala 100g', price: 85, stock: 300, sales: 189 },
  { id: 3, name: 'Tata Premium Tea 1kg', price: 380, stock: 160, sales: 156 },
  { id: 4, name: 'Haldiram Aloo Bhujia 400g', price: 110, stock: 175, sales: 142 },
  { id: 5, name: 'Aashirvaad Whole Wheat Atta 10kg', price: 450, stock: 200, sales: 128 },
]

const topBrands = [
  { name: 'India Gate', sales: '₹1,23,456', percentage: 28 },
  { name: 'Everest', sales: '₹98,234', percentage: 22 },
  { name: 'Tata', sales: '₹87,654', percentage: 20 },
  { name: 'Haldiram', sales: '₹76,543', percentage: 17 },
  { name: 'MDH', sales: '₹54,321', percentage: 13 },
]

export default function AdminDashboard() {
  const [currentTime, setCurrentTime] = useState(new Date())

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-gray-500 mt-1">
            Welcome back! Here&apos;s what&apos;s happening at Sri Sai Grocery
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-gray-500">
              {currentTime.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            <p className="text-lg font-semibold text-gray-800">
              {currentTime.toLocaleTimeString('en-IN')}
            </p>
          </div>
          <Link to="/admin/products" className="btn-primary flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Add Product
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="stat-card group cursor-pointer"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div className={`flex items-center gap-1 text-sm font-medium ${stat.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {stat.isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                {stat.change}
              </div>
            </div>
            <p className="text-gray-500 text-sm mb-1">{stat.title}</p>
            <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Products */}
        <div className="lg:col-span-2 card">
          <div className="p-6 border-b border-primary-100 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">Top Selling Products</h2>
            <Link to="/admin/products" className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center gap-1">
              View All <Eye className="w-4 h-4" />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="table-header">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Product</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Price</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Stock</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Sales</th>
                </tr>
              </thead>
              <tbody>
                {recentProducts.map((product) => (
                  <tr key={product.id} className="table-row">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-800">{product.name}</p>
                    </td>
                    <td className="px-6 py-4 text-gray-600">₹{product.price}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        product.stock > 100 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {product.stock} units
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800">{product.sales}</span>
                        <TrendingUp className="w-4 h-4 text-green-500" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Brands */}
        <div className="card">
          <div className="p-6 border-b border-primary-100">
            <h2 className="text-xl font-bold text-gray-800">Top Brands by Sales</h2>
          </div>
          <div className="p-6 space-y-4">
            {topBrands.map((brand, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-800">{brand.name}</span>
                  <span className="text-sm text-gray-500">{brand.sales}</span>
                </div>
                <div className="h-2 bg-primary-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-500"
                    style={{ width: `${brand.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/admin/products" className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl hover:shadow-lg transition-all group">
            <Package className="w-8 h-8 text-blue-600 mb-2 group-hover:scale-110 transition-transform" />
            <p className="font-medium text-gray-800">Add Product</p>
            <p className="text-sm text-gray-500">Create new product</p>
          </Link>
          <Link to="/admin/products" className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl hover:shadow-lg transition-all group">
            <DollarSign className="w-8 h-8 text-green-600 mb-2 group-hover:scale-110 transition-transform" />
            <p className="font-medium text-gray-800">Update Prices</p>
            <p className="text-sm text-gray-500">Modify product prices</p>
          </Link>
          <Link to="/admin/analytics" className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl hover:shadow-lg transition-all group">
            <TrendingUp className="w-8 h-8 text-purple-600 mb-2 group-hover:scale-110 transition-transform" />
            <p className="font-medium text-gray-800">View Analytics</p>
            <p className="text-sm text-gray-500">Sales trends</p>
          </Link>
          <Link to="/admin/products" className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl hover:shadow-lg transition-all group">
            <ShoppingCart className="w-8 h-8 text-orange-600 mb-2 group-hover:scale-110 transition-transform" />
            <p className="font-medium text-gray-800">Manage Stock</p>
            <p className="text-sm text-gray-500">Update inventory</p>
          </Link>
        </div>
      </div>
    </div>
  )
}

